import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, useStore } from 'react-redux'
import type { RecorderCallbacks, SummaryResult } from '@chewy/audio-to-notes-ui'
import { v4 as uuid } from 'uuid'

import { markDocumentToResendToCloudServices } from '~/api/ckeditor'
import { updateSoap } from '~/api/soapV2'
import SnackNotificationType from '~/constants/SnackNotificationType'
import { setDischargeNotes, setMedicalNotes } from '~/store/actions/soap'
import {
  getRecordingSoapId,
  setInitializingSession,
  setRecording,
  setRecordingSoapId,
  setSummarizing,
  setUpdatingSoapNotes,
} from '~/store/duck/audioRecorder'
import { addUiNotification } from '~/store/duck/uiNotifications'
import { getCurrentBusinessId } from '~/store/reducers/auth'
import { getPatientName } from '~/store/reducers/patients'
import {
  getDischargeNotes,
  getMedicalNotes,
  getPatientId,
  getSoapId,
} from '~/store/reducers/soap'
import { EditorDocumentType } from '~/types'
import { determineSummaryDestination } from '~/utils/summaryHandling'

import { useSummaryContext } from './useSummaryContext'

export interface AudioRecorderModalHandlers {
  onOpenMicPermissionModal: () => void
  onOpenNoAudioModal: (callbacks: {
    onContinueRecording?: () => void
    onEndRecording?: () => void
  }) => void
}

export const useAudioRecorderCallbacks = (
  modalHandlers: AudioRecorderModalHandlers,
): RecorderCallbacks => {
  const dispatch = useDispatch()
  const store = useStore()
  const { t } = useTranslation(['Common', 'Errors'])
  const context = useSummaryContext()
  const medicalNotes = useSelector(getMedicalNotes)
  const dischargeNotes = useSelector(getDischargeNotes)
  const patientId = useSelector(getPatientId)
  const patientName = useSelector(getPatientName(patientId))
  const businessId = useSelector(getCurrentBusinessId)
  const recordingSoapId = useSelector(getRecordingSoapId)
  // Read soapId directly from Redux inside the hook to ensure we always have the latest value
  // This avoids closure issues where context.soapId might be stale when recording starts
  const currentSoapId = useSelector(getSoapId)
  // Note: isRecording is available via callback parameters (data.isRecording), not needed from Redux here

  // Set recordingSoapId on SOAP page mount (for "Summarize" without recording)
  useEffect(() => {
    if (context.isOnSoapPage && currentSoapId && !recordingSoapId) {
      dispatch(setRecordingSoapId(currentSoapId))
    }
  }, [context.isOnSoapPage, currentSoapId, recordingSoapId, dispatch])

  // onBeforeSummarize
  const handleBeforeSummarize = useCallback(
    async (_params: {
      appName: string
      correlationId: string
      templates?: Record<string, string>
    }) =>
      // Return existing notes for context in transcript field
      [
        {
          summary: {
            type: 'soap' as const,
          },
          transcript: { text: medicalNotes || '' },
        },
        {
          summary: {
            type: 'discharge' as const,
          },
          transcript: { text: dischargeNotes || '' },
        },
      ],
    [medicalNotes, dischargeNotes],
  )

  // State tracking callbacks
  const handleRecordingStateChange = useCallback(
    (data: {
      appName: string
      correlationId: string
      isRecording: boolean
      recordId: string | null
    }) => {
      try {
        // Always update state - callbacks are source of truth
        // Redux state reflects actual recording state, not current page context
        dispatch(setRecording(data.isRecording))

        // Capture soapId when recording starts - this locks it to the SOAP where recording began
        // This ensures mutation updates the correct SOAP even if user navigates away
        if (data.isRecording && context.soapId) {
          dispatch(setRecordingSoapId(context.soapId))
        } else if (!data.isRecording) {
          // Clear recordingSoapId when recording stops (but keep it during summarization)
          // We'll clear it in onSessionChanged when session fully ends
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onRecordingStateChange', error)
        // Still update state to prevent stuck state
        dispatch(setRecording(false))
      }
    },
    [dispatch, context.soapId],
  )

  const handleSummarizationStateChange = useCallback(
    (data: {
      appName: string
      correlationId: string
      isSummarizing: boolean
      recordId: string | null
    }) => {
      try {
        dispatch(setSummarizing(data.isSummarizing))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSummarizationStateChange', error)
        dispatch(setSummarizing(false))
      }
    },
    [dispatch],
  )

  // onSessionChanged
  const handleSessionChanged = useCallback(
    (_data: {
      appName: string
      newCorrelationId: string
      newSessionId: string | null
      oldCorrelationId: string
      oldSessionId: string | null
    }) => {
      try {
        // Reset state when session changes (different appointment/correlationId)
        dispatch(setRecording(false))
        dispatch(setSummarizing(false))
        dispatch(setInitializingSession(false))
        dispatch(setRecordingSoapId(null))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSessionChanged', error)
        // Still reset state to prevent stuck state
        dispatch(setRecording(false))
        dispatch(setSummarizing(false))
        dispatch(setInitializingSession(false))
        dispatch(setRecordingSoapId(null))
      }
    },
    [dispatch],
  )

  // onCorrelationIdChange
  const handleCorrelationIdChange = useCallback(
    (data: {
      appName: string
      currentState: string
      isRecording: boolean
      isSummarizing: boolean
      newCorrelationId: string
      oldCorrelationId: string
      recordId: string | null
    }) => {
      // correlationId stays fixed during active states - this should not fire
      // eslint-disable-next-line no-console
      console.error('Unexpected correlationId change', data)
    },
    [],
  )

  // onComponentUnmount
  const handleComponentUnmount = useCallback(
    (_data: {
      appName: string
      correlationId: string
      hasActiveRecording: boolean
      hasActiveSummarization: boolean
      lifecycleState: string
      recordId: string | null
    }) => {
      // Reset initialization state on unmount
      dispatch(setInitializingSession(false))
      // Optional: Cleanup Portal-specific state
      // Library handles its own cleanup via config
    },
    [dispatch],
  )

  // onSummarizationStart
  const handleSummarizationStart = useCallback(
    (_data: {
      appName: string
      correlationId: string
      recordId: string | null
    }) => {
      // No-op: Previously captured patient name for mismatch modal, but modals removed
      // Mutation uses recordingSoapId which is already tracked
    },
    [],
  )

  // onSessionInit
  const handleSessionInit = useCallback(
    (_data: {
      appName: string
      correlationId: string
      hasRecording: boolean
      hasSummary: boolean
    }) => {
      // No-op: recordingSoapId is now set in onRecordingStateChange when recording starts
      // This prevents recordingSoapId from being overwritten when user navigates to different SOAP
    },
    [],
  )

  // onSummarizationComplete
  // Uses GraphQL mutation (updateSoap) to inject summaries into SOAP notes
  // CKEditor auto-syncs via WebSocket after mutation completes
  // IMPORTANT: Always uses recordingSoapId (SOAP where recording started), NOT currentSoapId
  // This ensures summaries are injected into the correct SOAP even if user navigates away
  const handleSummarizationComplete = useCallback(
    (data: {
      appName: string
      correlationId: string
      recordId: string | null
      summaries: SummaryResult[]
    }) => {
      try {
        // Update state first
        dispatch(setSummarizing(false))

        const { summaries, correlationId } = data

        // Use pure function to determine destination
        const destination = determineSummaryDestination(summaries, {
          pathname: context.pathname,
          currentAppointmentId: context.currentAppointmentId ?? null,
          isOnSoapPage: context.isOnSoapPage,
          summariesFTEnabled: context.summariesFTEnabled,
          correlationId,
        })

        // Helper function to run mutation - shared across all destination types
        // This ensures backend is updated even when user navigates away from SOAP page
        // CRITICAL: Uses recordingSoapId (SOAP where recording started), NOT currentSoapId
        // This ensures summaries are injected into the correct SOAP even if user navigated to a different SOAP
        // FALLBACK: If recordingSoapId is null (wasn't captured when recording started), use currentSoapId
        // This handles edge cases where recording started before soapId was available in Redux
        // Returns true if mutation succeeded, false otherwise
        const runMutation = async (): Promise<boolean> => {
          // Read recordingSoapId directly from Redux store to get the latest value
          // This handles timing issues where closure has stale value
          // CRITICAL: Only use recordingSoapId (SOAP where recording started), NOT currentSoapId
          // This ensures summaries are injected into the correct SOAP even if user navigated away
          const latestRecordingSoapId = getRecordingSoapId(store.getState())

          if (!latestRecordingSoapId) {
            // eslint-disable-next-line no-console
            console.error(
              '[AudioRecorder] Cannot run mutation - recordingSoapId is null',
              {
                recordingSoapId,
                latestRecordingSoapId,
                currentSoapId: context.soapId,
                pathname: context.pathname,
                isOnSoapPage: context.isOnSoapPage,
                correlationId,
              },
            )
            return false
          }

          // Extract medical and discharge notes from summaries
          const medicalSummary = summaries.find(
            (s: SummaryResult) => s.summaryType === 'soap',
          )
          const dischargeSummary = summaries.find(
            (s: SummaryResult) => s.summaryType === 'discharge',
          )

          // Treat empty strings as "no content" (|| converts to undefined)
          const medicalNotesText = medicalSummary?.summaryText || undefined
          const dischargeNotesText = dischargeSummary?.summaryText || undefined
          try {
            dispatch(setUpdatingSoapNotes(true))

            if (!businessId) {
              throw new Error('Business ID is required to update SOAP')
            }

            // Only include fields with values to avoid overwriting with undefined
            const input: {
              dischargeNotes?: string
              medicalNotes?: string
            } = {}
            if (medicalNotesText !== undefined && medicalNotesText !== null) {
              input.medicalNotes = medicalNotesText
            }
            if (
              dischargeNotesText !== undefined &&
              dischargeNotesText !== null
            ) {
              input.dischargeNotes = dischargeNotesText
            }
            // Validate that we have at least one field to update
            if (Object.keys(input).length === 0) {
              // eslint-disable-next-line no-console
              console.warn(
                '[AudioRecorder] No notes to update - skipping mutation',
              )
              dispatch(setUpdatingSoapNotes(false))
              return false
            }

            let result
            try {
              result = await updateSoap(businessId, {
                id: latestRecordingSoapId,
                input,
              })
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('[AudioRecorder] updateSoap mutation ERROR', error)
              throw error
            }
            // Update Redux state with mutation result
            if (!result) {
              // eslint-disable-next-line no-console
              console.error(
                '[AudioRecorder] Error updating SOAP via mutation',
                new Error('updateSoap returned falsy result'),
              )
              dispatch(setUpdatingSoapNotes(false))
              return false
            }

            // Update notes directly using setMedicalNotes/setDischargeNotes actions
            // These actions update Redux state without requiring full SoapData structure
            // Pass latestRecordingSoapId (SOAP where recording started) to track which SOAP was updated
            // so notes can be preserved when navigating back
            if (result.medicalNotes !== undefined) {
              dispatch(
                setMedicalNotes(result.medicalNotes, latestRecordingSoapId),
              )
            }
            if (result.dischargeNotes !== undefined) {
              dispatch(
                setDischargeNotes(result.dischargeNotes, latestRecordingSoapId),
              )
            }

            // Mark channels for resend to force fresh content from database on next initialization
            // This ensures when user navigates to SOAP page, channels will fetch fresh content
            // instead of loading stale content from Cloud Services
            // latestRecordingSoapId and businessId are guaranteed to be truthy at this point
            // (businessId checked at line 311, latestRecordingSoapId guaranteed via recordingSoapId check at line 281)
            // Note: markDocumentToResendToCloudServices only works if channels already exist in DB
            // If user hasn't navigated to SOAP page yet, channels won't exist and this will silently fail
            // This is OK - Redux sync will handle immediate updates, and channels will be marked on next init
            // Best-effort operations - errors are caught and return null
            await Promise.all([
              markDocumentToResendToCloudServices(
                businessId,
                EditorDocumentType.SOAP_MEDICAL,
                { soapId: latestRecordingSoapId },
              ).catch(() => null),
              markDocumentToResendToCloudServices(
                businessId,
                EditorDocumentType.SOAP_DISCHARGE,
                { soapId: latestRecordingSoapId },
              ).catch(() => null),
            ])

            // Clear loading state
            dispatch(setUpdatingSoapNotes(false))
            return true
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              '[AudioRecorder] Error updating SOAP via mutation',
              error,
            )
            dispatch(setUpdatingSoapNotes(false))
            return false
          }
        }
        switch (destination.type) {
          case 'discard':
            // FT disabled - do nothing with summaries (no mutation, no injection)

            return

          case 'queued':
            // Not on SOAP page - run mutation to update backend, then show toast

            ;(async () => {
              const success = await runMutation()

              if (success) {
                // Show toast with link to appointment only if mutation succeeded
                // Use latest recordingSoapId from Redux, fallback to appointmentId
                const latestRecordingSoapId = getRecordingSoapId(
                  store.getState(),
                )
                const appointmentId = destination.appointmentId || correlationId
                dispatch(
                  addUiNotification({
                    id: uuid(),
                    message: t('Common:AUDIO_RECORDER.SUMMARY_COMPLETED'),
                    type: SnackNotificationType.AUDIO_RECORDER_SUMMARY_COMPLETED,
                    params: {
                      soapId: latestRecordingSoapId || appointmentId,
                    },
                  }),
                )
              } else {
                // Show error toast if mutation failed
                dispatch(
                  addUiNotification({
                    id: uuid(),
                    message: t(
                      'Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY',
                    ),
                    type: SnackNotificationType.ERROR,
                  }),
                )
              }
            })().catch((error) => {
              // Log but don't break - mutation failure is handled in runMutation
              // eslint-disable-next-line no-console
              console.error('[AudioRecorder] Error in queued mutation', error)
              // Show error toast on unexpected errors
              dispatch(
                addUiNotification({
                  id: uuid(),
                  message: t('Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY'),
                  type: SnackNotificationType.ERROR,
                }),
              )
            })
            return

          case 'inject': // Update SOAP notes via GraphQL mutation
            // CKEditor will auto-sync via WebSocket when backend updates

            ;(async () => {
              // Capture recordingSoapId BEFORE async mutation to prevent race condition
              // where onSessionChanged could clear it during the async operation
              // If mutation succeeds, we know this soapId was valid (runMutation validates it)
              const capturedSoapId = getRecordingSoapId(store.getState())
              const success = await runMutation()

              if (success) {
                // Show success toast with link to SOAP page only if mutation succeeded
                // Use captured soapId (validated by runMutation before async operation)
                // This prevents race condition where onSessionChanged clears recordingSoapId
                // during async mutation, ensuring notification button can navigate
                dispatch(
                  addUiNotification({
                    id: uuid(),
                    message: t(
                      'Common:AUDIO_RECORDER.NOTES_UPDATED_SUCCESSFULLY',
                    ),
                    type: SnackNotificationType.AUDIO_RECORDER_NOTES_UPDATED,
                    params: {
                      soapId: capturedSoapId,
                    },
                  }),
                )
              } else {
                // Show error toast if mutation failed
                dispatch(
                  addUiNotification({
                    id: uuid(),
                    message: t(
                      'Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY',
                    ),
                    type: SnackNotificationType.ERROR,
                  }),
                )
              }
            })().catch((error) => {
              // Log but don't break - mutation failure is handled in runMutation
              // eslint-disable-next-line no-console
              console.error('[AudioRecorder] Error in inject mutation', error)
              // Show error toast on unexpected errors
              dispatch(
                addUiNotification({
                  id: uuid(),
                  message: t('Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY'),
                  type: SnackNotificationType.ERROR,
                }),
              )
            })
            break

          default:
            // Exhaustive check - all destination types handled
            break
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSummarizationComplete', error)
        // Still update state to prevent stuck state
        dispatch(setSummarizing(false))
        // Show error toast
        dispatch(
          addUiNotification({
            id: uuid(),
            message: t('Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY'),
            type: SnackNotificationType.ERROR,
          }),
        )
      }
    },
    [dispatch, t, context, recordingSoapId, businessId],
  )

  // getLockHolderName
  const getLockHolderName = useCallback(
    () =>
      // Return patient name to display in locked tabs ("Recording [name]...")
      patientName || undefined,
    [patientName],
  )

  // Modal callbacks
  const handleMicrophoneError = useCallback(
    (data: {
      appName: string
      correlationId: string
      error: Error
      errorCode: string
      recordId: string | null
    }) => {
      if (data.errorCode === 'PERMISSION_DENIED') {
        // Modal shows instructions on how to enable microphone access
        // Browser security prevents programmatically opening device settings
        modalHandlers.onOpenMicPermissionModal()
      }
    },
    [modalHandlers],
  )

  const handleNoAudioReceived = useCallback(
    (_data: {
      appName: string
      correlationId: string
      recordId: string | null
    }) => {
      modalHandlers.onOpenNoAudioModal({
        onContinueRecording: () => {
          // Intentionally empty - modal dismisses via onClose()
          // User can continue recording via library's AudioController UI
          // The library handles recording state internally
        },
        onEndRecording: () => {
          // TODO(SCRIBE-144): Implement stop recording without summarizing
          // Currently no way to programmatically stop recording from Portal callbacks
          // The library's AudioController handles recording control via its own UI
          // For now, modal dismisses via onClose() and user must stop via library UI
        },
      })
    },
    [modalHandlers],
  )

  // onError - Show "Doggo" error notification when status API or other errors occur
  const handleError = useCallback(
    (data: {
      appName: string
      correlationId: string
      error: Error
      recordId: string | null
    }) => {
      // eslint-disable-next-line no-console
      console.error('Audio Recorder Error:', {
        message: data.error.message,
        appName: data.appName,
        correlationId: data.correlationId,
        recordId: data.recordId,
      })

      // Show "Doggo" error notification (generic error for status API failures, etc.)
      dispatch(
        addUiNotification({
          id: uuid(),
          message: t('Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY'), // TODO: Add specific ERROR_GENERIC translation key
          type: SnackNotificationType.ERROR,
        }),
      )
    },
    [dispatch, t],
  )

  return {
    onBeforeSummarize: handleBeforeSummarize,
    onRecordingStateChange: handleRecordingStateChange,
    onSummarizationStateChange: handleSummarizationStateChange,
    onSessionChanged: handleSessionChanged,
    onCorrelationIdChange: handleCorrelationIdChange,
    onComponentUnmount: handleComponentUnmount,
    onSessionInit: handleSessionInit,
    onSummarizationStart: handleSummarizationStart,
    onSummarizationComplete: handleSummarizationComplete,
    getLockHolderName,
    onMicrophoneError: handleMicrophoneError,
    onNoAudioReceived: handleNoAudioReceived,
    onError: handleError,
    // Note: Add onRecordingTimeout when library supports it
  }
}
